import { storyLabel } from "@sts/geometry";
import { formatW, type CheckState } from "./design-check-types.js";
import type { RoomDesignCheck } from "./design-check-rooms.js";

/**
 * "Does this house work at 45 °C?" — the per-storey roll-up of the per-room
 * radiator check, for the flow-temperature control beside the plan.
 *
 * It adds NO physics. Every watt here comes from `checkRooms`, which corrects
 * each radiator's catalogue rating to the design water temperatures with
 * `correctEmitterOutput` — the BS EN 442 characteristic equation
 *
 *     output = rated(ΔT50) × (ΔT_actual / 50)^n,  ΔT_actual = (flow + return)/2 − room
 *
 * (see `emitters.ts` for the rule and its source). This module only groups
 * those rooms by storey and counts them, so that moving the flow temperature
 * shows the designer which floors stop working and by how much.
 *
 * COUNTING HONESTLY is the whole point of the shapes below. A roll-up that
 * says "6 / 6 sufficient" because four rooms could not be assessed is worse
 * than no roll-up at all, so a room lands in exactly one of six buckets and
 * only `sufficient` counts towards the numerator:
 *
 *   sufficient    the radiators drawn in it meet its heat loss at these
 *                 temperatures.
 *   short         they do not. `shortfallW` says by how much.
 *   no-emitter    the room has a heat loss but nothing is drawn in it. In the
 *                 denominator — an unheated room is a design that fails, not a
 *                 room to leave out of the count.
 *   not-assessed  radiators are drawn but their output cannot be worked out
 *                 (a size that is not in the catalogue, or one the catalogue
 *                 publishes no exponent for — no exponent is ever assumed
 *                 here). In the denominator, because it is a room the design
 *                 has not yet answered for.
 *   not-heated    the survey records the room with no heat loss at all (0 W).
 *                 There is nothing for an emitter to meet, so it is OUT of the
 *                 denominator and named separately rather than counted as a
 *                 pass.
 *   no-heat-loss  the scanned room has no survey room behind it (or the survey
 *                 has no heat loss yet), so nothing about it can be said. Out
 *                 of the denominator, and named.
 *
 * Scannix has no "this room is deliberately unheated" flag — a survey room is
 * always one of the MIS 3005-D Table 1 heated room types. `not-heated` is
 * therefore derived from the heat loss being zero, and says so in words rather
 * than claiming the designer marked anything.
 */

export type RoomSufficiency =
  | "sufficient"
  | "short"
  | "no-emitter"
  | "not-assessed"
  | "not-heated"
  | "no-heat-loss";

export interface StoreyRoomSufficiency {
  readonly story: number;
  /** The scanned room's index on its storey — with `story`, how the plan selects it. */
  readonly index: number;
  readonly name: string;
  readonly status: RoomSufficiency;
  readonly state: CheckState;
  /** The room's design heat loss, W. Absent when there is none to read. */
  readonly heatLossW?: number;
  /** The radiators' summed output at the chosen temperatures, W. */
  readonly outputW?: number;
  /** Watts short of the heat loss; 0 when it is met. */
  readonly shortfallW?: number;
  readonly radiatorCount: number;
  /** One line for this room, e.g. "Bathroom — 612 W of 900 W, short by 288 W". */
  readonly summary: string;
}

export interface StoreySufficiency {
  readonly story: number;
  /** "Ground floor", or the name someone typed for the storey. */
  readonly label: string;
  readonly rooms: readonly StoreyRoomSufficiency[];
  /** Rooms held to the rule: sufficient + short + no-emitter + not-assessed. */
  readonly roomsCounted: number;
  readonly sufficientRooms: number;
  /** Summed heat loss of the counted rooms, W. */
  readonly heatLossW: number;
  /** Summed emitter output of the counted rooms, W — a room with no output adds nothing. */
  readonly outputW: number;
  /** Summed per-room shortfalls, W. Not `heatLossW − outputW`: surplus in one room does not heat another. */
  readonly shortfallW: number;
  readonly roomsWithoutEmitter: readonly string[];
  readonly roomsNotAssessed: readonly string[];
  readonly roomsNotHeated: readonly string[];
  readonly roomsWithoutHeatLoss: readonly string[];
  readonly state: CheckState;
  /** "Emitter output 2,940 W of 4,200 W · sufficient rooms 2 / 4" */
  readonly summary: string;
}

function statusOf(room: RoomDesignCheck): RoomSufficiency {
  if (room.heatLossRoom === undefined) return "no-heat-loss";
  if (!(room.heatLossRoom.heatLossW > 0)) return "not-heated";
  if (room.radiators.length === 0) return "no-emitter";
  if (room.state === "pass") return "sufficient";
  if (room.state === "fail") return "short";
  return "not-assessed";
}

const STATE_OF: Readonly<Record<RoomSufficiency, CheckState>> = {
  sufficient: "pass",
  short: "fail",
  // A heated room with nothing in it is a design that does not heat it. Red,
  // not grey: grey would read as "we have not looked", and we have.
  "no-emitter": "fail",
  "not-assessed": "unknown",
  "not-heated": "unknown",
  "no-heat-loss": "unknown",
};

function roomSummary(room: RoomDesignCheck, status: RoomSufficiency): string {
  const load = room.heatLossRoom?.heatLossW;
  switch (status) {
    case "sufficient":
      return `${room.name} — ${formatW(room.totalOutputW ?? 0)} of ${formatW(load ?? 0)}, met`;
    case "short":
      return `${room.name} — ${formatW(room.totalOutputW ?? 0)} of ${formatW(load ?? 0)}, short by ${formatW(room.shortfallW ?? 0)}`;
    case "no-emitter":
      return `${room.name} — needs ${formatW(load ?? 0)}, no emitter on the plan (short by ${formatW(load ?? 0)})`;
    case "not-assessed":
      return `${room.name} — needs ${formatW(load ?? 0)}, emitter output not worked out`;
    case "not-heated":
      return `${room.name} — no heat loss recorded, so it is not counted as a heated room`;
    case "no-heat-loss":
      return `${room.name} — no heat loss for this room, so its emitters cannot be checked`;
  }
}

export interface StoreySufficiencyOptions {
  /** Names somebody typed for storeys, by level. Missing levels get the UK name. */
  readonly storeyLabels?: ReadonlyMap<number, string>;
}

/**
 * Every storey that has a room on it, lowest first, each with its rooms in the
 * order `checkRooms` produced them.
 */
export function summariseStoreys(
  rooms: readonly RoomDesignCheck[],
  opts: StoreySufficiencyOptions = {},
): readonly StoreySufficiency[] {
  const byStorey = new Map<number, RoomDesignCheck[]>();
  for (const room of rooms) {
    const list = byStorey.get(room.story) ?? [];
    list.push(room);
    byStorey.set(room.story, list);
  }

  return [...byStorey.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([story, list]) => storeyOf(story, list, opts));
}

function storeyOf(
  story: number,
  list: readonly RoomDesignCheck[],
  opts: StoreySufficiencyOptions,
): StoreySufficiency {
  const rooms: StoreyRoomSufficiency[] = list.map((room) => {
    const status = statusOf(room);
    const load = room.heatLossRoom?.heatLossW;
    // A room with no emitter is short by its whole heat loss. `checkRooms`
    // leaves `shortfallW` undefined there (it has no radiators to report on),
    // so the shortfall is taken from the load rather than read as zero.
    const shortfallW =
      status === "no-emitter"
        ? (load ?? 0)
        : status === "sufficient" || status === "short"
          ? (room.shortfallW ?? 0)
          : undefined;
    return {
      story: room.story,
      index: room.index,
      name: room.name,
      status,
      state: STATE_OF[status],
      ...(load !== undefined ? { heatLossW: load } : {}),
      ...(room.totalOutputW !== undefined ? { outputW: room.totalOutputW } : {}),
      ...(shortfallW !== undefined ? { shortfallW } : {}),
      radiatorCount: room.radiators.length,
      summary: roomSummary(room, status),
    };
  });

  const counted = rooms.filter(
    (r) =>
      r.status === "sufficient" ||
      r.status === "short" ||
      r.status === "no-emitter" ||
      r.status === "not-assessed",
  );
  const sufficientRooms = rooms.filter((r) => r.status === "sufficient").length;
  const heatLossW = counted.reduce((sum, r) => sum + (r.heatLossW ?? 0), 0);
  const outputW = counted.reduce((sum, r) => sum + (r.outputW ?? 0), 0);
  const shortfallW = counted.reduce((sum, r) => sum + (r.shortfallW ?? 0), 0);
  const namesOf = (status: RoomSufficiency): string[] =>
    rooms.filter((r) => r.status === status).map((r) => r.name);

  const state: CheckState =
    counted.length === 0
      ? "unknown"
      : sufficientRooms === counted.length
        ? "pass"
        : rooms.some((r) => r.status === "short" || r.status === "no-emitter")
          ? "fail"
          : "unknown";

  const summary =
    counted.length === 0
      ? `Emitter output: nothing on this storey can be checked yet`
      : `Emitter output: ${formatW(outputW)} of ${formatW(heatLossW)} · sufficient rooms: ${sufficientRooms} / ${counted.length}`;

  return {
    story,
    label: storyLabel(story, opts.storeyLabels?.get(story)),
    rooms,
    roomsCounted: counted.length,
    sufficientRooms,
    heatLossW,
    outputW,
    shortfallW,
    roomsWithoutEmitter: namesOf("no-emitter"),
    roomsNotAssessed: namesOf("not-assessed"),
    roomsNotHeated: namesOf("not-heated"),
    roomsWithoutHeatLoss: namesOf("no-heat-loss"),
    state,
    summary,
  };
}
